import React from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Radio,
  Button,
  Row,
  Col,
  Space,
  Switch,
  Tag,
} from 'antd';
import { GiftOutlined } from '@ant-design/icons';
import { ResponsiveDatePicker } from '../../../components/ResponsiveDatePicker/ResponsiveDatePicker';
import dayjs from 'dayjs';

const CouponFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  couponmode,
  categoryList = [],
  form,
  handleRandomId,
  couponTypeConfig = {},
}) => {
  return (
    <Modal
      title={
        <div className='custom-modal-title-box'>
          <div className='title-icon-wrapper'>
            <GiftOutlined />
          </div>
          <div>
            <div className='title-main'>{couponmode === 'add' ? 'เพิ่มคูปอง' : 'แก้ไขคูปอง'}</div>
            <div className='title-sub'>
              กำหนดรายละเอียด มูลค่า และเงื่อนไขการใช้สิทธิ์สำหรับลูกค้า
            </div>
          </div>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={780}
      centered
      className='custom-modal'
      destroyOnClose
    >
      <Form form={form} layout='vertical' onFinish={onSubmit} requiredMark={false}>
        {/* Section 1: ข้อมูลทั่วไปและประเภทคูปอง */}
        <div className='coupon-form-section'>
          <div className='coupon-form-section-header'>
            <span className='section-badge'>1</span>
            <span className='section-title'>ข้อมูลทั่วไปและประเภทคูปอง</span>
          </div>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name='coupon_id'
                label='รหัสคูปอง'
                tooltip='รหัสคูปองต้องมีความยาว 9 ตัวอักษร ใช้ตัวอักษร A-Z และตัวเลข 0-9'
                rules={[
                  { required: true, message: 'กรุณากรอก Coupon ID' },
                  { len: 9, message: 'Coupon ID ต้องมีความยาว 9 ตัวอักษร' },
                  { pattern: /^[A-Za-z0-9]+$/, message: 'ใช้ได้เฉพาะตัวอักษร A-Z และตัวเลข 0-9' },
                ]}
              >
                <Input
                  disabled={couponmode === 'edit'}
                  maxLength={9}
                  placeholder='เช่น MEYA99PRO'
                  style={{ textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}
                  suffix={
                    couponmode === 'add' && (
                      <Button
                        type='link'
                        size='small'
                        onClick={handleRandomId}
                        style={{ padding: 0, fontWeight: 500, fontSize: '12px' }}
                      >
                        🎲 สุ่มรหัส
                      </Button>
                    )
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name='coupon_type'
                label='ประเภทคูปอง'
                rules={[{ required: true, message: 'กรุณาเลือกประเภทคูปอง' }]}
              >
                <Select
                  placeholder='เลือกประเภทคูปอง'
                  onChange={(value) => {
                    if (value === 'free_shipping') {
                      form.setFieldsValue({ discount: 0 });
                    }
                  }}
                >
                  <Select.Option value='product'>ลดราคาสินค้า</Select.Option>
                  <Select.Option value='shipping'>ลดค่าขนส่ง</Select.Option>
                  <Select.Option value='free_shipping'>ฟรีค่าจัดส่ง</Select.Option>
                  <Select.Option value='cashback'>💰 เงินคืนลดรอบหน้า (Cashback)</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name='expiredate'
                label='วันหมดอายุ'
                rules={[{ required: true, message: 'กรุณาเลือกวันหมดอายุ' }]}
              >
                <ResponsiveDatePicker
                  format='YYYY-MM-DD'
                  style={{ width: '100%' }}
                  placeholder='เลือกวันหมดอายุ'
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name='productcategory_id'
                label='หมวดหมู่สินค้าที่ร่วมรายการ'
                tooltip='สามารถเลือกได้หลายหมวดหมู่ หากไม่เลือกจะใช้ได้กับสินค้าทุกชิ้น'
              >
                <Select
                  mode='multiple'
                  placeholder='ทุกหมวดหมู่ (ค่าเริ่มต้น)'
                  allowClear
                  maxTagCount='responsive'
                >
                  {categoryList.map((cat) => (
                    <Select.Option key={cat.productcategory_id} value={cat.productcategory_id}>
                      {cat.productcategory_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name='allowed_payment_methods'
                label='ช่องทางการชำระเงินที่ร่วมรายการ'
                tooltip='เลือกช่องทางที่อนุญาตให้ใช้คูปองนี้ (เว้นว่าง = ใช้ได้ทุกช่องทาง)'
              >
                <Select
                  mode='multiple'
                  placeholder='ทุกช่องทางการชำระเงิน (ค่าเริ่มต้น)'
                  allowClear
                  maxTagCount='responsive'
                >
                  <Select.Option value='creditcard'>💳 บัตรเครดิต / เดบิต</Select.Option>
                  <Select.Option value='promtpay'>📱 สแกน QR พร้อมเพย์</Select.Option>
                  <Select.Option value='cash'>💵 เงินสด / เก็บเงินปลายทาง</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} style={{ marginTop: '4px' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '12px',
                }}
              >
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #e8e8e8',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: '#262626', fontSize: '13px' }}>
                      🌐 แสดงในรายการให้ลูกค้ากดใช้ (Public)
                    </div>
                    <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                      เปิด = เห็นในหน้าเลือกคูปอง / ปิด = ลูกค้าต้องกรอกรหัสเอง
                    </div>
                  </div>
                  <Form.Item name='is_public_display' valuePropName='checked' noStyle>
                    <Switch checkedChildren='แสดง' unCheckedChildren='ซ่อน' defaultChecked />
                  </Form.Item>
                </div>

                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #e8e8e8',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: '#262626', fontSize: '13px' }}>
                      🔗 ใช้ร่วมกับคูปองอื่นได้ (Stackable)
                    </div>
                    <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                      เปิด = ใช้ร่วมกับคูปองส่งฟรี/ส่วนลดอื่นได้ / ปิด = ใช้เดี่ยวเท่านั้น
                    </div>
                  </div>
                  <Form.Item name='is_stackable' valuePropName='checked' noStyle>
                    <Switch checkedChildren='เปิด' unCheckedChildren='ปิด' defaultChecked />
                  </Form.Item>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Section 2: เงื่อนไขและมูลค่าส่วนลด */}
        <div className='coupon-form-section'>
          <div className='coupon-form-section-header'>
            <span className='section-badge'>2</span>
            <span className='section-title'>มูลค่าส่วนลดและเงื่อนไขการใช้สิทธิ์</span>
          </div>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name='min_order'
                label='ยอดสั่งซื้อขั้นต่ำ'
                tooltip='ยอดสั่งซื้อขั้นต่ำที่ต้องมีเพื่อใช้คูปองนี้'
                rules={[{ required: true, message: 'กรุณากรอกยอดสั่งซื้อขั้นต่ำ' }]}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  placeholder='เช่น 500'
                  addonBefore='฿'
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.coupon_type !== currentValues.coupon_type
                }
              >
                {({ getFieldValue }) => {
                  const couponType = getFieldValue('coupon_type');
                  const isFreeShipping = couponType === 'free_shipping';
                  const isCashback = couponType === 'cashback';

                  if (isCashback) {
                    return (
                      <Row gutter={8}>
                        <Col span={14}>
                          <Form.Item
                            name='cashback_percent'
                            label='เงินคืนรอบหน้า (%)'
                            tooltip='เปอร์เซ็นต์เงินคืนสำหรับออกคูปองส่วนลดรอบหน้า เช่น 3.25%'
                            rules={[{ required: true, message: 'กรุณากรอก % เงินคืน' }]}
                          >
                            <InputNumber
                              min={0.01}
                              max={100}
                              step={0.25}
                              style={{ width: '100%' }}
                              placeholder='เช่น 3.25'
                              addonAfter='%'
                            />
                          </Form.Item>
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            name='max_discount'
                            label='จำกัดสูงสุด'
                            tooltip='จำนวนเงินคืนสูงสุดต่อออเดอร์ (เว้นว่าง = ไม่จำกัด)'
                          >
                            <InputNumber
                              min={1}
                              style={{ width: '100%' }}
                              placeholder='฿ ไม่จำกัด'
                              addonBefore='฿'
                              formatter={(value) =>
                                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                              }
                              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    );
                  }

                  return (
                    <Form.Item
                      name='discount'
                      label={isFreeShipping ? 'ส่วนลดค่าจัดส่ง (ฟรีค่าจัดส่ง)' : 'มูลค่าส่วนลด'}
                      tooltip={
                        isFreeShipping
                          ? 'สำหรับคูปองฟรีค่าจัดส่ง ระบบจะลดค่าจัดส่งเต็มจำนวนโดยอัตโนมัติ'
                          : 'จำนวนเงินส่วนลดที่จะหักลบออกจากยอดสั่งซื้อ'
                      }
                      rules={
                        isFreeShipping ? [] : [{ required: true, message: 'กรุณากรอกมูลค่าส่วนลด' }]
                      }
                    >
                      <InputNumber
                        min={0}
                        style={{ width: '100%' }}
                        placeholder={isFreeShipping ? '0 (ฟรีค่าจัดส่ง)' : 'เช่น 100'}
                        addonBefore='฿'
                        disabled={isFreeShipping}
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: '8px' }}>
            <Col xs={24} sm={12}>
              <Form.Item
                name='usage_limit'
                label='โควตาการใช้งานทั้งหมด'
                tooltip='จำนวนครั้งที่คูปองนี้สามารถถูกใช้งานได้ทั้งหมดในระบบ'
                rules={[{ required: true, message: 'กรุณาเลือกโควตาการใช้งาน' }]}
              >
                <Radio.Group>
                  <Space direction='vertical'>
                    <Radio value={1}>ใช้งานได้ครั้งเดียว (Single-use)</Radio>
                    <Radio value={-1}>ไม่จำกัดจำนวนครั้ง (Unlimited)</Radio>
                    <Radio value={0}>กำหนดจำนวนครั้งเอง (Custom)</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.usage_limit !== cur.usage_limit}>
                {({ getFieldValue }) => {
                  if (getFieldValue('usage_limit') === 0) {
                    return (
                      <Form.Item
                        name='custom_usage_limit'
                        label='ระบุจำนวนครั้ง'
                        rules={[{ required: true, message: 'กรุณากรอกจำนวนครั้ง' }]}
                      >
                        <InputNumber
                          min={2}
                          max={9999}
                          placeholder='ระบุจำนวนครั้ง เช่น 5'
                          style={{ width: '100%', marginTop: '6px' }}
                          addonAfter='ครั้ง'
                        />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name='per_user_limit'
                label='สิทธิ์การใช้งานต่อคน'
                tooltip='จำกัดสิทธิ์การใช้งานต่อ 1 บัญชีผู้ซื้อ'
                rules={[{ required: true, message: 'กรุณาเลือกสิทธิ์ต่อคน' }]}
              >
                <Radio.Group>
                  <Radio value={1}>1 สิทธิ์ / คน</Radio>
                  <Radio value={-1}>ไม่จำกัด</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Section 3: Live Preview Ticket */}
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) =>
            prev.coupon_id !== cur.coupon_id ||
            prev.coupon_type !== cur.coupon_type ||
            prev.discount !== cur.discount ||
            prev.min_order !== cur.min_order ||
            prev.expiredate !== cur.expiredate ||
            prev.productcategory_id !== cur.productcategory_id ||
            prev.cashback_percent !== cur.cashback_percent ||
            prev.is_stackable !== cur.is_stackable ||
            prev.allowed_payment_methods !== cur.allowed_payment_methods
          }
        >
          {({ getFieldsValue }) => {
            const vals = getFieldsValue();
            const code = vals.coupon_id || 'MEYAXXXXX';
            const type = vals.coupon_type || 'product';
            const disc =
              type === 'free_shipping'
                ? 'ฟรีค่าจัดส่ง'
                : type === 'cashback'
                  ? `คืน ${vals.cashback_percent || 0}%`
                  : vals.discount
                    ? `฿${Number(vals.discount).toLocaleString()}`
                    : '฿0';
            const min = vals.min_order || 0;
            const exp =
              vals.expiredate && dayjs(vals.expiredate).isValid()
                ? dayjs(vals.expiredate).format('YYYY-MM-DD')
                : '-';
            const cats = Array.isArray(vals.productcategory_id) ? vals.productcategory_id : [];
            const pms = Array.isArray(vals.allowed_payment_methods)
              ? vals.allowed_payment_methods
              : [];
            const isStackable = vals.is_stackable !== false;

            const pmLabels = {
              creditcard: '💳 บัตรเครดิต',
              promtpay: '📱 พร้อมเพย์',
              cash: '💵 เงินสด',
            };

            return (
              <div className='coupon-live-ticket'>
                <div className='ticket-left'>
                  <div className='ticket-discount-badge'>{disc}</div>
                  <div className='ticket-type-label'>
                    {couponTypeConfig[type]?.label || 'ลดราคา'}
                  </div>
                </div>
                <div className='ticket-divider'></div>
                <div className='ticket-right'>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
                  >
                    <span className='ticket-code'>{code.toUpperCase()}</span>
                    <Tag
                      color={isStackable ? 'geekblue' : 'purple'}
                      style={{ margin: 0, fontSize: '11px' }}
                    >
                      {isStackable ? '🔗 ใช้ร่วมได้' : '🔒 ใช้เดี่ยว'}
                    </Tag>
                  </div>
                  <div className='ticket-rules'>
                    <span>ขั้นต่ำ ฿{Number(min).toLocaleString()}</span> •{' '}
                    <span>หมดอายุ {exp}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                    {pms.length > 0 ? (
                      pms.map((pm) => (
                        <Tag color='orange' key={pm} style={{ margin: 0, fontSize: '10px' }}>
                          {pmLabels[pm] || pm}
                        </Tag>
                      ))
                    ) : (
                      <Tag color='default' style={{ margin: 0, fontSize: '10px' }}>
                        ทุกช่องทางชำระเงิน
                      </Tag>
                    )}
                  </div>
                  <div className='ticket-cats'>
                    {cats.length === 0 ? (
                      <span className='ticket-cat-item'>ทุกหมวดหมู่</span>
                    ) : (
                      cats.map((cid) => {
                        const cat = categoryList.find(
                          (c) => (c.productcategory_id || '').trim() === (cid || '').trim(),
                        );
                        return (
                          <span className='ticket-cat-item' key={cid}>
                            {cat ? cat.productcategory_name : cid}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            );
          }}
        </Form.Item>

        <div className='modal-footer-custom-modern'>
          <Button size='large' onClick={onClose}>
            ยกเลิก
          </Button>
          <Button
            type='primary'
            size='large'
            htmlType='submit'
            loading={loading}
            icon={<GiftOutlined />}
          >
            {couponmode === 'add' ? 'บันทึกสร้างคูปอง' : 'บันทึกการแก้ไข'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

CouponFormModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  loading: PropTypes.bool,
  couponmode: PropTypes.string,
  categoryList: PropTypes.arrayOf(
    PropTypes.shape({
      productcategory_id: PropTypes.string,
      productcategory_name: PropTypes.string,
    }),
  ),
  form: PropTypes.object,
  handleRandomId: PropTypes.func,
  couponTypeConfig: PropTypes.object,
};

export default CouponFormModal;
